import { Component, signal } from '@angular/core';
import { SetupWizardUserForm } from '../../../../../forms/setup-wizard/setup-wizard-user-form/setup-wizard-user-form';
import { SetupWizardUsersTable } from './setup-wizard-users-table/setup-wizard-users-table';
import { SetupWizard } from '../../../../../../services/setup-wizard';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import {
	LocationCsvKeys,
	SectionCsvKeys,
	SetupWizardLocation,
	SetupWizardPayloadObjectKeys,
	SetupWizardSection,
	SetupWizardUser,
	SetupWizardUserFormPayload,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SetupWizardUploadCsvButton } from '../../setup-wizard-upload-csv-button/setup-wizard-upload-csv-button';
import { SetupWizardDownloadCsvButton } from '../../setup-wizard-download-csv-button/setup-wizard-download-csv-button';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import {
	makeEnrolledColumn,
	makeHistoricalColumn,
	makeUsersColumn,
	SetupWizardSectionsTable,
} from '../setup-wizard-section-editor/setup-wizard-sections-table/setup-wizard-sections-table';
import {
	SetupWizardLocationsTable,
	makeAssignedColumn,
} from '../setup-wizard-location-editor/setup-wizard-locations-table/setup-wizard-locations-table';

@Component({
	selector: 'app-setup-wizard-user-editor',
	imports: [
		SetupWizardUserForm,
		SetupWizardUsersTable,
		NzButtonModule,
		SetupWizardUploadCsvButton,
		SetupWizardDownloadCsvButton,
		NzTabsModule,
		SetupWizardSectionsTable,
		NzButtonModule,
		SetupWizardLocationsTable,
	],
	standalone: true,
	templateUrl: './setup-wizard-user-editor.html',
	styleUrl: './setup-wizard-user-editor.scss',
})
export class SetupWizardUserEditor {
	public edit = signal<SetupWizardUser | null>(null);
	public showForm = signal<boolean>(false);
	public loading = signal<boolean>(false);
	private clientUuid: string | undefined;
	private sub?: Subscription;
	public SetupWizardPayloadObjectKeys = SetupWizardPayloadObjectKeys;
	public selectedTabIndex = signal<number>(0);
	public sectionTableColumns = signal<string[]>([
		SectionCsvKeys.name,
		SectionCsvKeys.language,
		SectionCsvKeys.activeStartDate,
		SectionCsvKeys.activeEndDate,
		SectionCsvKeys.sectionCode,
		SectionCsvKeys.allowSelfRostering,
		SectionCsvKeys.active,
		makeUsersColumn().key,
		makeEnrolledColumn().key,
		makeHistoricalColumn().key,
	]);
	public locationTableColumns = signal<string[]>([
		LocationCsvKeys.name,
		LocationCsvKeys.active,
		makeUsersColumn().key,
		makeAssignedColumn().key,
		makeEnrolledColumn().key,
		makeHistoricalColumn().key,
	]);

	constructor(
		private route: ActivatedRoute,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	public onFormSubmit(payload: SetupWizardUserFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.users, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onFormClear() {
		this.showForm.set(false);
		this.edit.set(null);
	}

	public onAdd() {
		this.showForm.set(true);
		this.edit.set(null);
	}

	public onEditSections($event: SetupWizardUser) {
		this.onEdit($event);
		this.selectedTabIndex.set(1);
	}

	public onEditLocations($event: SetupWizardUser) {
		this.onEdit($event);
		this.selectedTabIndex.set(2);
	}

	public onEdit($event: SetupWizardUser) {
		if ($event.after || $event.before) {
			this.edit.set($event);
			this.showForm.set(true);
		}
	}

	public async onToggleDisable($event: SetupWizardUser) {
		const beforeAfter = $event.after || $event.before;

		if (!beforeAfter) {
			throw new Error('No user data available to update');
		}

		await this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.users, {
			...beforeAfter,
			active: !beforeAfter.active,
		});
	}

	public async onReset($event: SetupWizardUser) {
		if (!$event.after) {
			throw new Error('No user data available to reset');
		}

		await this.setupWizard.resetPayloadItem(SetupWizardPayloadObjectKeys.users, $event.after);
	}

	public async onRemove($event: SetupWizardUser) {
		if (!$event.after) {
			throw new Error('No user data available to remove');
		}

		await this.setupWizard.removePayloadItem(SetupWizardPayloadObjectKeys.users, $event.after);
	}

	public async onEnroll($event: { user: SetupWizardUser; section: SetupWizardSection }) {
		await this.setupWizard.enroll($event.user, $event.section);
	}

	public async onUnenroll($event: { user: SetupWizardUser; section: SetupWizardSection }) {
		await this.setupWizard.unenroll($event.user, $event.section);
	}

	public async onAssign($event: { user: SetupWizardUser; location: SetupWizardLocation }) {
		await this.setupWizard.assign($event.user, $event.location);
	}

	public async onUnassign($event: { user: SetupWizardUser; location: SetupWizardLocation }) {
		await this.setupWizard.unassign($event.user, $event.location);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
